import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AUTH_REDIRECT_DELAY_MS, AuthErrorFallback } from '@components/auth-error-fallback';
import { AuthLoadingSkeleton } from '@components/auth-loading-skeleton';
import { REDIRECT_URL, SELECT_VENUE_ROUTE } from '@constants/routes';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { useChangeUser } from '@hooks/auth/use-change-user';
import { useTokenManagement } from '@hooks/auth/use-token-management';
import { useGetUser } from '@hooks/user/user-get-user';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { setCurrencyPreferences } from '@utils/price-formatter/price-formatter';

import { AuthContextValue } from './auth.type';

export const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isImpersonated: false,
  signOut: () => {},
  changeUser: () => {},
  resetAdminUser: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { getTokens, setTokens, clearTokens } = useTokenManagement();

  const { isPublicEventReportPage } = useContext(FeatureFlagContext);
  const { setUserState, setIsUserLoading } = useContext(UserContext);

  // Get current tokens to determine authentication state
  const tokens = getTokens();

  // User is authenticated if they have a valid access token
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!tokens.accessToken;
  });
  const [isRedirecting, setIsRedirecting] = useState(false);
  const redirectUrlRef = useRef('');
  const isErrorRedirectRef = useRef(false);
  const [impersonateUser, setImpersonateUser] = useState<{ email: string; uuid: string }>({ email: '', uuid: '' });

  const { data: impersonateCredentials } = useChangeUser({
    uuid: impersonateUser.uuid,
    options: { enabled: !!impersonateUser.uuid && !isPublicEventReportPage },
  });

  // Enable user query if we have any token (access or refresh)
  // The refresh flow will handle token renewal automatically
  const hasAnyToken = !!(tokens.accessToken ?? tokens.refreshToken);

  const {
    data: user,
    isLoading: isUserLoading,
    isError,
  } = useGetUser({
    enabled: hasAnyToken && !isPublicEventReportPage,
    retry: 2,
  });

  const isImpersonated =
    !!getTokens({ isImpersonate: true }).accessToken || !!getTokens({ isImpersonate: true }).refreshToken;

  const signOut = useCallback(() => {
    const currentUrl = globalThis.location.href;
    clearTokens();
    queryClient.clear();
    redirectUrlRef.current = `${REDIRECT_URL}=${currentUrl}`;
    setIsRedirecting(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearTokens, queryClient]);

  const changeUser = ({ email, uuid }: { email: string; uuid: string }) => {
    const { accessToken, refreshToken } = getTokens();
    if (accessToken && refreshToken) {
      setTokens({ accessToken, refreshToken, isImpersonate: true });
      setImpersonateUser({ email, uuid });
    }
  };

  const resetAdminUser = () => {
    const { refreshToken: adminRefreshToken, accessToken: adminAccessToken } = getTokens({ isImpersonate: true });
    if (adminAccessToken || adminRefreshToken) {
      setTokens({ accessToken: adminAccessToken ?? undefined, refreshToken: adminRefreshToken ?? '' });
    }
    clearTokens({ isImpersonate: true });
    globalThis.location.reload();
  };

  useEffect(() => {
    if (impersonateCredentials) {
      setTokens({
        accessToken: impersonateCredentials.accessToken,
        refreshToken: impersonateCredentials.refreshToken,
      });
      globalThis.open(SELECT_VENUE_ROUTE, '_self');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonateCredentials]);

  useEffect(() => {
    if (isPublicEventReportPage) setIsUserLoading(false);
    else setIsUserLoading(isUserLoading);
    if (user) {
      Sentry.setUser({ email: user.email, id: user.id });
      setUserState(user);
      i18n.changeLanguage(user.lang);
      setCurrencyPreferences(user.currencyFormat, user.currencyDisplay, user.lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, i18n, isUserLoading, isPublicEventReportPage]);

  useEffect(() => {
    if (isPublicEventReportPage) {
      setIsAuthenticated(true);
      return;
    }

    // Only sign out if there's no refresh token (can't recover)
    // Don't sign out on isError alone - the refresh token flow will handle 401s
    const currentTokens = getTokens();
    if (!currentTokens.refreshToken) {
      signOut();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicEventReportPage]);

  // If user query fails after all retries, trigger sign out to redirect to login
  useEffect(() => {
    if (isError && !isPublicEventReportPage) {
      isErrorRedirectRef.current = true;
      signOut();
    }
  }, [isError, isPublicEventReportPage, signOut]);

  // Redirect to login when signing out
  // Error redirects wait AUTH_REDIRECT_DELAY_MS so the user sees the error page;
  // voluntary logouts redirect immediately.
  useEffect(() => {
    if (!isRedirecting) return;

    const loginUrl = redirectUrlRef.current || `${REDIRECT_URL}=${globalThis.location.href}`;
    const delay = isErrorRedirectRef.current ? AUTH_REDIRECT_DELAY_MS : 0;
    const timer = setTimeout(() => {
      globalThis.location.href = loginUrl;
    }, delay);

    return () => clearTimeout(timer);
  }, [isRedirecting]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        signOut,
        changeUser,
        resetAdminUser,
        isImpersonated,
      }}>
      {isRedirecting ? (
        <AuthLoadingSkeleton>{isErrorRedirectRef.current ? <AuthErrorFallback /> : null}</AuthLoadingSkeleton>
      ) : isPublicEventReportPage || user ? (
        children
      ) : (
        <AuthLoadingSkeleton />
      )}
    </AuthContext.Provider>
  );
};
